<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import type { Artwork as ArtworkType } from '@amp/web-app-components/src/components/Artwork/types';
    import ExplicitBadge from '@amp/web-app-components/src/components/Explicit/ExplicitBadge.svelte';
    import Artwork from '@amp/web-app-components/src/components/Artwork/Artwork.svelte';
    import LineClamp from '@amp/web-app-components/src/components/LineClamp/LineClamp.svelte';
    import SeparatedList from '@amp/web-app-components/src/components/helpers/SeparatedList.svelte';
    import allowDrag from '@amp/web-app-components/src/actions/allow-drag';
    import type { DragOptions } from '@amp/web-app-components/src/actions/allow-drag';
    import { getRafQueue } from '@amp/web-app-components/src/utils/rafQueue';

    export let artwork: ArtworkType | 'slot';
    export let profile: string = 'episode-lockup';
    export let ordinal: string | undefined;
    export let title: string;
    export let subtitleLinks: Titled[];
    export let isExplicit: boolean;
    export let explicitAriaText: string;
    export let translateFn: (str: string, values?: object) => string;
    export let dragOptions: DragOptions | false = false;
    export let immediatePlayModule: boolean = false;
    export let ariaLabel: string | undefined | null = null;
    export let titleHref: string | undefined = '';
    export let contextMenuIsOpen: boolean = false;
    export let typography: string | undefined | null = null;

    interface Titled {
        id?: string;
        title: string;
        segue?: unknown;
        href?: string | undefined;
    }

    const dispatch = createEventDispatcher();
    const rafQueue = getRafQueue();

    let multilineTitle = true; // Default to 2 lines title and 1 line subtitle
    let multilineSubtitle = false; // Used for conditional focus styling on subtitle

    // If a content is Explicit, prefix "Explicit" in front of the title for auditory users, so that they are
    // immediately aware of the explicit nature of the content.
    // TODO: rdar://101556171 (Onyx Music: Use shared AMP.Shared.ContentA.Comma.ContentB loc key)
    $: ariaText = isExplicit
        ? translateFn('AMP.Shared.ContentA.Comma.ContentB', {
              contentA: translateFn('FUSE.Explicit'),
              contentB: ariaLabel ?? title,
          })
        : ariaLabel;
    $: hasLinkableSubtitle = subtitleLinks?.some((l) => l.href || l.segue);

    // Used for conditional focus styling on subtitle
    $: singleSubtitle = subtitleLinks?.length === 1;

    // Schedule multiline updates with RAF queue
    $: updateMultilineTitle = (e: CustomEvent) =>
        rafQueue.add(() => (multilineTitle = e.detail.multiline));

    $: updateMultilineSubtitle = (e: CustomEvent) =>
        rafQueue.add(() => (multilineSubtitle = e.detail.multiline));

    /**
     * @event click - triggered when user clicks on editorial card
     * @type {{ url: string, isExternal: boolean|null }} clickable link
     */
    export let linkAction: {
        url: string;
        isExternal?: boolean | null;
    } = null;

    $: linkProps = linkAction?.isExternal
        ? {
              target: '_blank',
              rel: 'noopener noreferer',
          }
        : {};

    const handleTitleClick = (event: MouseEvent): void => {
        dispatch('titleClick', { event });
    };

    const handleWrapperClick = (event: MouseEvent): void => {
        dispatch('wrapperClick', { event });
    };

    const handleSubtitleClick =
        (link) =>
        (event: MouseEvent): void => {
            if (link.segue) {
                dispatch('subtitleClick', { event, link });
            }
        };
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
    class="product-lockup"
    class:has-open-menu={contextMenuIsOpen}
    class:has-body-text={typography === 'body'}
    aria-label={ariaText || title}
    data-testid="product-lockup"
    on:click={handleWrapperClick}
    use:allowDrag={dragOptions}
>
    <div
        class="product-lockup__artwork"
        aria-hidden={!($$slots['play-button'] || $$slots['context-button'])}
        class:has-controls={$$slots['play-button'] || $$slots['context-button']}
    >
        <slot name="artwork" alt={title} {profile}>
            {#if artwork !== 'slot'}
                <Artwork {artwork} {profile} alt={ariaText || title} />
            {/if}
        </slot>

        <slot name="artwork-metadata-overlay" />

        <div data-testid="lockup-control" class="product-lockup__controls">
            {#if linkAction}
                <a
                    class="product-lockup__link"
                    data-testid="product-lockup-link"
                    aria-label={ariaText}
                    href={linkAction.url ?? '#'}
                    on:click|stopPropagation={handleWrapperClick}
                    {...linkProps}
                >
                    {title}
                </a>
            {/if}
            {#if $$slots['play-button']}
                <div
                    data-testid="play-button"
                    class="product-lockup__play-button"
                    class:product-lockup__play-button--large={immediatePlayModule}
                >
                    <slot name="play-button" />
                </div>
            {/if}
            {#if $$slots['context-button']}
                <div
                    data-testid="context-button"
                    class="product-lockup__context-button"
                >
                    <slot name="context-button" />
                </div>
            {/if}
        </div>
    </div>

    <div class="product-lockup__content">
        {#if ordinal}
            <p
                data-testid="product-lockup-ordinal"
                class="product-lockup__ordinal"
            >
                {ordinal}
            </p>
        {/if}
        <div
            class="product-lockup__content-details"
            class:product-lockup__content-details--no-subtitle-link={!hasLinkableSubtitle}
            dir="auto"
        >
            <div
                class="product-lockup__title-link"
                class:product-lockup__title-link--multiline={multilineTitle}
            >
                <LineClamp
                    clamp={2}
                    allowFocusOverflow={true}
                    on:resize={updateMultilineTitle}
                    observe={true}
                >
                    {#if titleHref}
                        <a
                            href={titleHref}
                            data-testid="product-lockup-title"
                            class="product-lockup__title"
                            on:click|stopPropagation={handleTitleClick}
                            {...linkProps}
                        >
                            {title || ''}
                        </a>
                    {:else}
                        <span
                            data-testid="product-lockup-title"
                            class="product-lockup__title link"
                        >
                            {title || ''}
                        </span>
                    {/if}
                    <slot name="favorited-badge" slot="badge" />
                </LineClamp>

                {#if isExplicit}
                    <div class="product-lockup__explicit">
                        <ExplicitBadge {explicitAriaText} />
                    </div>
                {/if}
            </div>

            <!-- rdar://78549345 (Wire up provider actions for ProductLockup) -->
            {#if subtitleLinks && subtitleLinks.length > 0}
                <p
                    data-testid="product-lockup-subtitles"
                    class="product-lockup__subtitle-links"
                    class:product-lockup__subtitle-links--multiline={multilineSubtitle}
                    class:product-lockup__subtitle-links--singlet={singleSubtitle}
                >
                    <LineClamp
                        clamp={multilineTitle ? 1 : 2}
                        allowFocusOverflow={true}
                        on:resize={updateMultilineSubtitle}
                        observe={true}
                    >
                        <SeparatedList
                            items={subtitleLinks}
                            {translateFn}
                            let:item={link}
                        >
                            {#if link.href}
                                <a
                                    href={link.href}
                                    class="product-lockup__subtitle"
                                    class:link={link.segue}
                                    data-testid="product-lockup-subtitle"
                                    on:click|stopPropagation={handleSubtitleClick(
                                        link,
                                    )}
                                    {...linkProps}
                                >
                                    {link.title}
                                </a>
                            {:else if link.segue}
                                <button
                                    data-testid="product-lockup-subtitle"
                                    class="product-lockup__subtitle product-lockup__subtitle--button link"
                                    on:click|stopPropagation={handleSubtitleClick(
                                        link,
                                    )}
                                >
                                    <!--
                                        Workaround for rdar://100779462 (Regression : Library albums with multiple artists should show ellipsis in line2)
                                        `-webkit-line-clamp` fails when applied to
                                        <button>, but will still apply to contents
                                        wrapped in <span>.
                                    -->
                                    <span>{link.title}</span>
                                </button>
                            {:else}
                                <span
                                    data-testid="product-lockup-subtitle"
                                    class="product-lockup__subtitle"
                                    >{link.title}</span
                                >
                            {/if}
                        </SeparatedList>
                    </LineClamp>
                </p>
            {/if}
        </div>
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'amp/stylekit/core/box-shadows' as *;
    @use 'amp/stylekit/core/mixins/hover-style' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/scrim-opacity-controller' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;
    @use '@amp/web-shared-styles/app/core/mixins/focus' as *;

    .product-lockup {
        height: 100%;
        min-width: 0; // Prevent grid/flex blowout
        cursor: pointer;

        @include scrim-opacity-controller;

        :global(.social-badges) {
            transition: var(--global-transition);

            @include scrim-opacity($inverted: true);
        }
    }

    .product-lockup__link {
        position: absolute;
        display: block;
        width: 100%;
        height: 100%;
        color: transparent;
        z-index: var(--z-default);
    }

    .product-lockup__artwork {
        position: relative;
        box-shadow: $global-default-content-box-shadow;
        border-radius: var(
            --global-border-radius-medium,
            #{$global-border-radius-medium}
        );
        z-index: var(--z-default);

        &::after {
            @include content-container-hover-style;
            @include scrim-opacity; // Override mixin opacity
        }

        :global(.artwork-component) {
            --override-placeholder-bg-color: var(--artwork-bg-color);
        }
    }

    .product-lockup__content {
        margin-top: 4px;
    }

    .product-lockup__ordinal {
        color: var(--systemPrimary);
        font: var(--callout);
        font-weight: 800; // heavy
    }

    .product-lockup__content-details {
        text-align: start;
    }

    .product-lockup__content-details--no-subtitle-link {
        &:hover {
            --linkHoverTextDecoration: underline;
        }

        // Apply text decoration to each element separately so decoration
        // color is consistent with text color
        .product-lockup__title-link,
        .product-lockup__subtitle-links {
            text-decoration: var(--linkHoverTextDecoration);
        }
    }

    .product-lockup__title-link {
        display: grid;

        // Second column Explicit badge collapses if not present
        grid-template-columns: minmax(0, 1fr) auto;
        color: var(--systemPrimary);
        font: var(--callout);

        // Hide multi-line text artifacts when link is focused
        :global(.multiline-clamp--overflow:focus-within) {
            --overflowBleedSize: 3px;
        }
    }

    .has-body-text {
        .product-lockup__title-link,
        .product-lockup__subtitle-links {
            font: var(--body);
        }
    }

    .product-lockup__title-link--multiline,
    .product-lockup__subtitle-links--multiline.product-lockup__subtitle-links--singlet {
        // Progressive enhancement for nicer-looking focus indicators on links
        // that wrap to 2 lines. Instead of drawing the indicator around the inline
        // element, we draw it around the block that contains the element instead.
        //
        // (Unfortunately, we can't apply this trick when there are multiple subtitle
        // links, as we need to be able to individually focus each one.)
        @supports #{'selector(:has(:focus-visible))'} {
            // Remove focus styles from inline children
            :focus-visible {
                outline: none;
                box-shadow: none;
            }

            // Apply focus styles to block parent
            &:has(:focus-visible) {
                &.product-lockup__title-link {
                    @include focus-shadow;
                }

                // Targets the clamp container and is inset for correct positioning
                &.product-lockup__subtitle-links :global(.multiline-clamp) {
                    @include focus-shadow-inset;
                }
            }
        }
    }

    .product-lockup__subtitle-links {
        margin-top: 1px;
        color: var(--systemSecondary);
        font: var(--callout);
    }

    .product-lockup__subtitle--button {
        display: inline;
        text-align: start;

        // Workaround for rdar://100779462 (Regression : Library albums with
        // multiple artists should show ellipsis in line2)
        //
        // `-webkit-line-clamp` fails when applied to <button>, but will still
        // apply to contents wrapped in <span>.
        > span {
            // `--mc-lineClamp` property is set in <LineClamp> component
            @include line-clamp(var(--mc-lineClamp));
        }
    }

    .product-lockup__explicit {
        --explicitBadgeSize: 11px;
        margin-inline-start: 10px;
    }

    .product-lockup__controls {
        width: 100%;
        height: 100%;
        top: 0;
        position: absolute;
        border-radius: inherit;
        transition: var(--global-transition);
        z-index: calc(var(--z-default) + 1);

        @include scrim-opacity;

        // Prevent any interaction with controls on touchscreen devices
        @media (--target-touchscreen) {
            visibility: hidden;
        }
    }

    .product-lockup__context-button {
        bottom: 10px;
        position: absolute;
        line-height: 0; // Reset to normalize container height
        inset-inline-end: 10px;
        z-index: var(--z-default);
    }

    .product-lockup__play-button {
        --iconCircleFillBGOverride: transparent; // Transparent to allow play-button material to be visible

        :global(.play-button.is-large) {
            height: 100%;
            width: 100%;
            border-radius: 0;
        }
        bottom: 10px;
        position: absolute;
        line-height: 0; // Reset to normalize container height
        inset-inline-start: 10px;
        z-index: var(--z-default);
    }

    .product-lockup__play-button--large {
        @include absolute-center;
    }
</style>
